import { useParams } from 'react-router-dom';
import { getLocalTimeZone, today } from '@internationalized/date';
import { type FormApi } from '@tanstack/react-form';
import { type zodValidator } from '@tanstack/zod-form-adapter';
import invariant from 'tiny-invariant';

import {
  type BffEditPolicyRequest,
  type BffGetConfigurationsResponseContainerModeDisplayModel,
  type BffGetConfigurationsResponseCoveragePackage,
  type BffGetConfigurationsResponseModeOfTransportOption,
} from '../../../generated/api-client';
import { type Policy } from '../../../model/Policy';
import { getJourneyUpdatePayload } from '../../components/RouteDetails/utils';
import { getUpdatedValue } from '../../utils/forms';
import { type PolicyRouteParams } from '../PolicyDetailsPage/PolicyDetailsPage';
import { type PolicyEditFormProps } from './PolicyEditForm/types';
import { getDefaultPortOrPlace } from './utils';

export const editableFields: (keyof PolicyEditFormProps)[] = [
  'cargoDescription',
  'bookingReference',
  'letterOfCredit',
  'issueDate',
  'eta',
  'marksAndNumbers',
];

export const getDefaultFormData = ({
  policy,
  defaultPrimaryMot,
  defaultLoadingMot,
  defaultDeliveryMot,
  defaultUserCurrency,
  defaultCoveragePackage,
  defaultContainerMode,
  defaultDistributorId,
}: {
  policy: Policy | null | undefined;
  defaultPrimaryMot: BffGetConfigurationsResponseModeOfTransportOption;
  defaultLoadingMot: BffGetConfigurationsResponseModeOfTransportOption;
  defaultDeliveryMot: BffGetConfigurationsResponseModeOfTransportOption;
  defaultUserCurrency: string;
  defaultCoveragePackage?: BffGetConfigurationsResponseCoveragePackage;
  defaultContainerMode?: BffGetConfigurationsResponseContainerModeDisplayModel['title'];
  defaultDistributorId?: number;
}) => {
  return {
    division: policy?.quote?.division,
    profitCenter: policy?.quote?.profit_center,
    etd: policy?.quote?.etd || '',
    eta: policy?.quote?.eta || '',
    vesselName: policy?.quote?.vessel_name || '',
    marksAndNumbers: policy?.quote?.goods_marks || '',
    bookingReference: policy?.quote?.external_reference || '',

    distributorId: defaultDistributorId,

    assuredName: {
      company_name: policy?.customer?.name || '',
      id: policy?.customer?.id || undefined,
    },

    origin: policy?.quote
      ? getDefaultPortOrPlace(policy?.quote, 'origin')
      : undefined,
    destination: policy?.quote
      ? getDefaultPortOrPlace(policy?.quote, 'destination')
      : undefined,
    primaryMot:
      policy?.quote?.primary_transport_mode_code &&
      policy?.quote?.primary_transport_mode_code_display_name
        ? {
            name: policy?.quote?.primary_transport_mode_code,
            display_name:
              policy?.quote?.primary_transport_mode_code_display_name,
          }
        : defaultPrimaryMot,

    placeOfLoading:
      policy?.quote && policy?.quote.loading_place
        ? {
            type: 'place',
            place: {
              place_id: policy?.quote.loading_place.provider_place_uuid,
              description: policy?.quote.loading_place.full_address,
            },
            port: null,
          }
        : undefined,
    placeOfLoadingMot: defaultLoadingMot,

    placeOfDelivery:
      policy?.quote && policy?.quote.delivery_place
        ? {
            type: 'place',
            place: {
              place_id: policy?.quote.delivery_place.provider_place_uuid,
              description: policy?.quote.delivery_place.full_address,
            },
            port: null,
          }
        : undefined,
    placeOfDeliveryMot: defaultDeliveryMot,

    placeOfLoadingPdfOverride: policy?.quote?.loading_pdf_certificate_override,
    placeOfDeliveryPdfOverride:
      policy?.quote?.delivery_pdf_certificate_override,
    destinationPdfOverride: policy?.quote?.destination_pdf_certificate_override,
    originPdfOverride: policy?.quote?.origin_pdf_certificate_override,

    cargoDescription: policy?.quote?.commodity_external_description || '',
    cargoCurrency: policy?.quote?.commodity_currency ?? defaultUserCurrency,
    cargoValue: policy?.quote?.commodity_value,
    assuredAddress: {
      description: policy?.quote?.customer?.address?.full_address || '',
      place_id: policy?.quote?.customer?.address?.provider_place_uuid || '',
    },
    letterOfCredit: policy?.quote?.letter_of_credit || '',
    issueDate: policy?.issue_date ?? today(getLocalTimeZone()).toString(),
    vessel: policy?.quote?.vessel
      ? {
          name: policy.quote.vessel.name,
          external_id: policy.quote.vessel.external_id,
        }
      : {
          name: policy?.quote?.vessel_name,
          external_id: undefined,
        },
    commodity: {
      id: policy?.quote?.commodity_category_id,
      title: policy?.quote?.commodity_category,
    },
    containerMode: defaultContainerMode?.toString(),
    coveragePackage: defaultCoveragePackage,

    freightCost: policy?.quote?.freight_cost,
    freightCostCurrency:
      policy?.quote?.freight_cost_currency ?? defaultUserCurrency,

    dutyCost: policy?.quote?.duty_cost,
    dutyCostCurrency: policy?.quote?.duty_cost_currency ?? defaultUserCurrency,
  } satisfies PolicyEditFormProps;
};

export const usePolicyEditFormLogic = () => {
  const { policyId } = useParams<PolicyRouteParams>();

  const getPayload = (
    form: FormApi<PolicyEditFormProps, typeof zodValidator>,
  ): BffEditPolicyRequest => {
    const data = form.state.values;
    const formMeta = form.state.fieldMeta;

    invariant(policyId, 'policyId is required for update');

    return {
      ...getJourneyUpdatePayload(form),

      etd: getUpdatedValue(data.etd, formMeta, 'etd') ?? undefined,
      eta: getUpdatedValue(data.eta, formMeta, 'eta') ?? undefined,
      // @ts-expect-error - TODO HACK: generated api doesn't support null values until we upgrade to pydantic v2
      external_reference: getUpdatedValue(
        data.bookingReference,
        formMeta,
        'bookingReference',
      ),
      // @ts-expect-error - TODO HACK: generated api doesn't support null values until we upgrade to pydantic v2
      commodity_external_description: getUpdatedValue(
        data.cargoDescription,
        formMeta,
        'cargoDescription',
      ),
      // @ts-expect-error - TODO HACK: generated api doesn't support null values until we upgrade to pydantic v2
      goods_marks: getUpdatedValue(
        data.marksAndNumbers,
        formMeta,
        'marksAndNumbers',
      ),
      // @ts-expect-error - TODO HACK: generated api doesn't support null values until we upgrade to pydantic v2
      letter_of_credit: getUpdatedValue(
        data.letterOfCredit,
        formMeta,
        'letterOfCredit',
      ),
      // @ts-expect-error - TODO HACK: generated api doesn't support null values until we upgrade to pydantic v2
      issue_date: getUpdatedValue(data.issueDate, formMeta, 'issueDate'),
      // @ts-expect-error - TODO HACK: generated api doesn't support null values until we upgrade to pydantic v2
      division: getUpdatedValue(data.division, formMeta, 'division'),
      // @ts-expect-error - TODO HACK: generated api doesn't support null values until we upgrade to pydantic v2
      profit_center: getUpdatedValue(
        data.profitCenter,
        formMeta,
        'profitCenter',
      ),
    };
  };

  return {
    getPayload,
    policyId,
  };
};
